import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Channel,
  ChannelList,
  InfiniteScroll,
  MessageInput,
  MessageList,
  Thread,
  useChatContext,
  useChannelStateContext,
  Window,
} from "stream-chat-react";
import { ChannelFilters, ChannelOptions, ChannelSort } from "stream-chat";
import { cn } from "@/lib/utils";
import { CustomListItem } from "@/components/pages/chats/component/chat-custom-components/CustomListItem";
import { GuestChannelHeader } from "@/components/pages/chats/component/chat-custom-components/GuestChannelHeader";
import {
  ChatFilters,
  ChatFilterType,
} from "@/components/pages/chats/component/chat-custom-components/ChatFilters";
import "stream-chat-react/dist/css/v2/index.css";
import CustomMessageInput from "@/components/pages/chats/component/chat-custom-components/CustomMessageInput";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { CustomMessageUi } from "@/components/pages/chats/component/chat-custom-components/CustomMessageUi";
import { encodeToMp3 } from "stream-chat-react/mp3-encoder";
import { useTranslation } from "react-i18next";
import {
  createMessageSearchFunction,
  MessageSearchResultItem,
} from "@/components/pages/chats/utils/message-search";

interface ChatComponentProps {
  userId: string;
  activeChannel?: string | null;
  className?: string;
}

interface ChannelStateWatcherProps {
  onUpdate: () => void;
  watchMembership?: boolean;
}

const ChannelStateWatcher = ({
  onUpdate,
  watchMembership,
}: ChannelStateWatcherProps) => {
  const { channel } = useChannelStateContext();

  useEffect(() => {
    if (!channel) return;

    const handleUpdate = () => onUpdate();

    channel.on("channel.updated", handleUpdate);
    if (watchMembership) {
      channel.on("member.updated", handleUpdate);
    }

    return () => {
      channel.off("channel.updated", handleUpdate);
      channel.off("member.updated", handleUpdate);
    };
  }, [channel, onUpdate, watchMembership]);

  return null;
};

export const ChatsComponent: FC<ChatComponentProps> = ({
  userId,
  className,
  activeChannel,
}) => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const [activeFilter, setActiveFilter] = useState<ChatFilterType>("inbox");
  const [pinnedOnly, setPinnedOnly] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = useCallback(
    () => setRefreshKey((prev) => prev + 1),
    [],
  );

  const audioRecordingConfig = useMemo(
    () => ({ transcoderConfig: { encoder: encodeToMp3 } }),
    [],
  );

  const options = useMemo<ChannelOptions>(
    () => ({ presence: true, state: true }),
    [],
  );
  const sort = useMemo<ChannelSort>(
    () => [{ pinned_at: -1 }, { last_message_at: -1 }],
    [],
  );

  const { userProfile } = useAuthenticatedUser();
  const authenticatedUserId = userProfile?.id;

  const filters = useMemo<ChannelFilters>(() => {
    const baseFilters = {
      members: { $in: [userId] },
      type: "messaging",
      ...(pinnedOnly ? { pinned: true } : {}),
    };

    switch (activeFilter) {
      case "inbox":
        return {
          ...baseFilters,
          $or: [{ resolved: { $exists: false } }, { resolved: false }],
        };

      case "assigned_to_me":
        return {
          ...baseFilters,
          assigned_to_id: authenticatedUserId,
        };

      case "resolved":
        return {
          ...baseFilters,
          resolved: { $eq: true },
        };

      case "all":
        return baseFilters;

      default:
        return baseFilters;
    }
  }, [userId, activeFilter, authenticatedUserId, pinnedOnly]);

  const channelSearchProps = useMemo(
    () => ({
      placeholder: t("chats.searchMessages"),
      searchForChannels: false,
      searchForUsers: false,
      searchFunction: createMessageSearchFunction({
        client,
        filters,
        options,
      }),
      SearchResultItem: MessageSearchResultItem,
    }),
    [client, filters, options, t],
  );

  return (
    <div
      className={cn("flex h-full min-h-0 flex-row overflow-hidden", className)}
    >
      <div className={"flex h-full min-h-0 w-full max-w-1/3 flex-col"}>
        <ChatFilters
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          pinnedOnly={pinnedOnly}
          onPinnedOnlyChange={setPinnedOnly}
        />
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pt-2">
          <ChannelList
            key={`list-${activeFilter}-${pinnedOnly}-${refreshKey}`}
            Preview={CustomListItem}
            Paginator={InfiniteScroll}
            sort={sort}
            filters={filters}
            options={options}
            showChannelSearch
            additionalChannelSearchProps={channelSearchProps}
            customActiveChannel={activeChannel ?? undefined}
          />
        </div>
      </div>
      <div className={"flex min-h-0 min-w-0 grow flex-col overflow-hidden"}>
        <Channel Message={CustomMessageUi}>
          <ChannelStateWatcher
            onUpdate={triggerRefresh}
            watchMembership={pinnedOnly}
          />
          <Window>
            <GuestChannelHeader />
            <MessageList returnAllReadData />
            <MessageInput
              Input={(props) => (
                <CustomMessageInput
                  {...props}
                  hideRequestButtons={false}
                  hideResolvePanel={false}
                />
              )}
              asyncMessagesMultiSendEnabled
              audioRecordingEnabled
              audioRecordingConfig={audioRecordingConfig}
            />
          </Window>
          <Thread />
        </Channel>
      </div>
    </div>
  );
};
